import Image from 'next/image';
import type { ReactElement } from 'react';

export interface PeekGridItem {
  /** Cover image URL. Empty string renders the neutral fallback. */
  thumbnailSrc: string;
  /** Title rendered at the bottom of the card over a gradient. */
  title: string;
  /** A11y description. Defaults to `title` when omitted. */
  thumbnailAlt?: string;
}

export interface PeekGridProps {
  /**
   * Up to four items are rendered in a 4-column grid (stacks to 2
   * columns under 640px). Extras beyond four are clipped — consumers
   * surface overflow via `overflowNote`.
   */
  items: ReadonlyArray<PeekGridItem>;
  /**
   * Optional mono caption rendered below the grid, e.g.
   * `"// + 84 more locked episodes"`.
   */
  overflowNote?: string;
}

const MAX = 4;

/**
 * 4-column grid of blurred peek cards — used in the PaywallDialog
 * top section to show the user what they're unlocking. Each card
 * shows a blurred thumb, a numbered mono badge, and a title.
 *
 * Renders `null` when `items` is empty so callers can omit the
 * surrounding section cleanly.
 */
export function PeekGrid({ items, overflowNote }: PeekGridProps): ReactElement | null {
  if (items.length === 0) return null;
  const clipped = items.slice(0, MAX);
  return (
    <>
      <div className="tb-peek-grid" aria-hidden="true">
        {clipped.map((item, i) => {
          const alt = item.thumbnailAlt ?? item.title;
          return (
            <div className="tb-peek-card" key={`${item.title}-${i}`}>
              {item.thumbnailSrc.length > 0 ? (
                <Image
                  src={item.thumbnailSrc}
                  alt={alt}
                  fill
                  sizes="180px"
                  style={{ objectFit: 'cover' }}
                />
              ) : null}
              <span className="tb-peek-num">{String(i + 1).padStart(2, '0')}</span>
              <span className="tb-peek-title">{item.title}</span>
            </div>
          );
        })}
      </div>
      {overflowNote !== undefined ? <div className="tb-paywall-note">{overflowNote}</div> : null}
    </>
  );
}
